import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './ManagerContractMobile.css';
import { Card, Spinner } from 'react-bootstrap';
import { FaTint, FaLayerGroup, FaClipboardCheck, FaHeartbeat, FaCheckCircle } from 'react-icons/fa';
import Select from 'react-select';
import API_BASE_URL from './config/api';

import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import BlueHeader from './Components/Header/BlueHeader';
import Sidebar from './Components/Sidebar/Sidebar';
import SearchBar from './Components/SearchBar/SearchBar';
import StackCleaningTable from './Components/StackCleaningTable/StackCleaningTable';

// Color palette for stack badges (matching StackCleaningTable)
const STACK_COLORS = [
  { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' }, // Blue
  { bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4' }, // Pink
  { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' }, // Green
  { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' }, // Amber
  { bg: '#e0e7ff', color: '#3730a3', border: '#a5b4fc' }, // Indigo
  { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' }, // Orange
  { bg: '#f3e8ff', color: '#6b21a8', border: '#d8b4fe' }, // Purple
  { bg: '#ccfbf1', color: '#115e59', border: '#5eead4' }, // Teal
  { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' }, // Red
  { bg: '#ecfccb', color: '#3f6212', border: '#bef264' }, // Lime
];

// Hash function to get consistent color index for a stack number
const getStackColorIndex = (stackNumber) => {
  if (!stackNumber) return 0;
  const str = String(stackNumber);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash) % STACK_COLORS.length;
};

const getStackColor = (stackNumber) => {
  const index = getStackColorIndex(stackNumber);
  return STACK_COLORS[index];
};

// Circular Progress component for health scores
function CircularProgress({ percentage, size = 70, color }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  // Color logic based on percentage
  const getColor = (score) => {
    if (score <= 20) return '#dc2626'; // red
    if (score <= 40) return '#ea580c'; // orange
    if (score <= 60) return '#d97706'; // yellow-orange
    if (score <= 80) return '#65a34a'; // yellow-green
    return '#16a34a'; // green
  };
  
  const strokeColor = color || getColor(percentage);
  
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth="5"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={strokeColor}
        strokeWidth="5"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      {/* Percentage text */}
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="16"
        fontWeight="700"
        fill="#374151"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}
      >
        {percentage}%
      </text>
    </svg>
  );
}

// Helper to extract cycle type value
const getCycleTypeValue = (cycle = {}) => {
  const rawType =
    cycle.Type ||
    cycle.type ||
    cycle.inspectionType ||
    cycle.inspection_type ||
    cycle.contractType ||
    cycle.contract_type ||
    cycle.serviceType ||
    cycle.service_type ||
    '';
  return typeof rawType === 'string' ? rawType.toLowerCase() : '';
};

const isKitchenStackCycle = (cycle) => getCycleTypeValue(cycle).includes('kitchen stack');

// Building Wireframe Component
function BuildingWireframe({ unitsData, getStackColor, hoveredUnitId, onHoverUnit, onUnitClick, hoveredStackId, onHoverStack, searchTerm, buildingName, statusFilter, onStatusFilterChange, selectedStacks, onStackClick }) {
  // Check if a unit matches the search term
  const isUnitSearchMatch = (unit) => {
    if (!searchTerm || searchTerm.trim() === '') return false;
    const searchLower = searchTerm.toLowerCase().trim();
    const unitNum = (unit.unit_number || '').toLowerCase();
    return unitNum.includes(searchLower);
  };
  // Extract unique stacks and sort them
  const stacks = [...new Set(unitsData.map(u => u.stack_number).filter(Boolean))].sort((a, b) => {
    // Try numeric sort first
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return String(a).localeCompare(String(b));
  });

  // Helper to extract floor from unit number
  // Unit 501 = Floor 5, Unit 1001 = Floor 10, Unit 1215 = Floor 12
  const getFloorFromUnit = (unitNumber) => {
    const numStr = String(unitNumber || '').replace(/\D/g, ''); // Remove non-digits
    if (numStr.length <= 2) return parseInt(numStr, 10) || 0; // If 1-2 digits, it's the floor itself
    // All digits except last 2 are the floor
    const floorStr = numStr.slice(0, -2);
    return parseInt(floorStr, 10) || 0;
  };

  // Extract floors from unit numbers
  const floors = unitsData
    .map(u => getFloorFromUnit(u.unit_number))
    .filter(f => f > 0);

  // Calculate min and max floors
  const minFloorNum = floors.length > 0 ? Math.min(...floors) : 1;
  const maxFloorNum = floors.length > 0 ? Math.max(...floors) : 10;

  // Round to nearest 5 for grid lines
  const minFloorLine = Math.floor(minFloorNum / 5) * 5 || 0;
  // Round the top up to the nearest 5, then add 5 more floors of headroom so the
  // building visually extends to a "roof" above the top unit. Without this, the
  // highest-floor units sit flush against the top edge and overlap the angled
  // stack labels.
  const maxFloorLine = Math.ceil(maxFloorNum / 5) * 5 + 5;
  
  // Generate floor lines (every 5 floors) from top to bottom
  const floorLines = [];
  for (let floor = maxFloorLine; floor >= minFloorLine; floor -= 5) {
    floorLines.push(floor);
  }
  // Ensure we have at least some lines
  if (floorLines.length < 2) {
    floorLines.push(0, 5, 10);
  }

  // If no data, show empty state
  if (stacks.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        height: '100%',
        minHeight: '400px',
        color: '#9ca3af'
      }}>
        <FaTint style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
        <p style={{ fontSize: '1rem', textAlign: 'center' }}>
          No stack data available
        </p>
      </div>
    );
  }

  // Fixed viewBox dimensions - matches typical container aspect ratio (~1:1)
  const LABEL_WIDTH = 35;
  const STACK_LABEL_HEIGHT = 75; // Space for title and stack labels
  const PADDING = 35;
  const STACK_SPACING = Math.max(80, Math.min(120, 600 / stacks.length)); // Stack spacing
  const DEPTH_OFFSET = 35; // 3D effect
  const shouldAngleLabels = stacks.length > 12 || STACK_SPACING < 90; // Angle labels when cramped
  const BUILDING_COLOR = '#374151';
  const DEPTH_COLOR = '#d1d5db';
  const LINE_COLOR = '#9ca3af';

  const gridWidth = PADDING * 2 + (stacks.length - 1) * STACK_SPACING;
  const gridHeight = gridWidth * 0.85; // Slightly taller than wide for better fit

  // Total SVG dimensions for viewBox - minimal padding
  const svgWidth = LABEL_WIDTH + gridWidth + DEPTH_OFFSET + 5;
  const svgHeight = STACK_LABEL_HEIGHT + gridHeight + DEPTH_OFFSET + 5;

  return (
    <div className="stack-wireframe" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header Row - Title on left, Filter buttons on right */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: '0.5rem',
        marginBottom: '1.0rem',
        flexShrink: 0,
        padding: '0 0.5rem'
      }}>
        {/* Title */}
        <h6 style={{ 
          fontWeight: 600, 
          margin: 0,
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.9rem'
        }}>
        <div style={{
          width: '16px',
          height: '16px',
          backgroundColor: '#dbeafe',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '8px',
          color: '#3b82f6'
        }}>
          <FaTint />
        </div>
        {buildingName || 'Building'} Stack Visualization
        </h6>

        {/* Legend - Clickable to filter */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          fontSize: '0.85rem',
          color: '#6b7280'
        }}>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.4rem',
              cursor: 'pointer',
              padding: '4px 10px',
              borderRadius: '6px',
              backgroundColor: statusFilter === 'ok' ? '#ecfdf5' : 'transparent',
              border: statusFilter === 'ok' ? '2px solid #16a34a' : '2px solid transparent',
              transition: 'all 0.15s ease'
            }}
            onClick={() => onStatusFilterChange && onStatusFilterChange(statusFilter === 'ok' ? null : 'ok')}
          >
            <div style={{ 
              width: '14px', 
              height: '14px', 
              borderRadius: '50%', 
              backgroundColor: '#ecfdf5',
              border: '2px solid #16a34a'
            }} />
            <span style={{ fontWeight: statusFilter === 'ok' ? 600 : 400 }}>OK</span>
          </div>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.4rem',
              cursor: 'pointer',
              padding: '4px 10px',
              borderRadius: '6px',
              backgroundColor: statusFilter === 'issue' ? '#fef2f2' : 'transparent',
              border: statusFilter === 'issue' ? '2px solid #dc2626' : '2px solid transparent',
              transition: 'all 0.15s ease'
            }}
            onClick={() => onStatusFilterChange && onStatusFilterChange(statusFilter === 'issue' ? null : 'issue')}
          >
            <div style={{ 
              width: '14px', 
              height: '14px', 
              borderRadius: '50%', 
              backgroundColor: '#fef2f2',
              border: '2px solid #dc2626'
            }} />
            <span style={{ fontWeight: statusFilter === 'issue' ? 600 : 400 }}>Issue Found</span>
          </div>
        </div>
      </div>

      {/* Wireframe Container - fixed height, width fills container */}
      <div className="stack-wireframe-canvas" style={{
        width: '100%',
        height: '640px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <svg 
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ 
            display: 'block', 
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        >
          {/* Axis Label - Vertical Stacks (top) */}
          <text
            x={LABEL_WIDTH + gridWidth / 2}
            y={18}
            textAnchor="middle"
            fontSize="22"
            fontWeight="600"
            fill="#6b7280"
          >
            Vertical Stacks
          </text>

          {/* Axis Label - Floors (left side, rotated) */}
          <text
            x={18}
            y={STACK_LABEL_HEIGHT + gridHeight / 2}
            textAnchor="middle"
            fontSize="20"
            fontWeight="600"
            fill="#6b7280"
            transform={`rotate(-90, 18, ${STACK_LABEL_HEIGHT + gridHeight / 2})`}
          >
            Floors
          </text>
          {/* 3D Depth - Filled polygons for solid depth effect */}
          {/* Top depth face (trapezoid) */}
          <polygon
            points={`
              ${LABEL_WIDTH},${STACK_LABEL_HEIGHT}
              ${LABEL_WIDTH + DEPTH_OFFSET},${STACK_LABEL_HEIGHT - DEPTH_OFFSET}
              ${LABEL_WIDTH + gridWidth + DEPTH_OFFSET},${STACK_LABEL_HEIGHT - DEPTH_OFFSET}
              ${LABEL_WIDTH + gridWidth},${STACK_LABEL_HEIGHT}
            `}
            fill="#9ca3af"
            stroke="#6b7280"
            strokeWidth={2}
          />
          {/* Right depth face (trapezoid) */}
          <polygon
            points={`
              ${LABEL_WIDTH + gridWidth},${STACK_LABEL_HEIGHT}
              ${LABEL_WIDTH + gridWidth + DEPTH_OFFSET},${STACK_LABEL_HEIGHT - DEPTH_OFFSET}
              ${LABEL_WIDTH + gridWidth + DEPTH_OFFSET},${STACK_LABEL_HEIGHT + gridHeight - DEPTH_OFFSET}
              ${LABEL_WIDTH + gridWidth},${STACK_LABEL_HEIGHT + gridHeight}
            `}
            fill="#6b7280"
            stroke="#4b5563"
            strokeWidth={2}
          />

          {/* Building Outline (front face) */}
          <rect
            x={LABEL_WIDTH}
            y={STACK_LABEL_HEIGHT}
            width={gridWidth}
            height={gridHeight}
            fill="#fafafa"
            stroke="#374151"
            strokeWidth={2}
            rx={4}
          />

          {/* Vertical Lines (Stacks) - dotted riser lines */}
          {stacks.map((stack, idx) => {
            const colors = getStackColor(stack);
            const x = LABEL_WIDTH + PADDING + idx * STACK_SPACING;
            const isStackSelected = selectedStacks.includes(stack);
            const isStackHighlighted = hoveredStackId === stack || isStackSelected;
            const isStackDimmed = selectedStacks.length > 0 && !isStackSelected;
            return (
              <g 
                key={`stack-${stack}`}
                style={{ cursor: 'pointer', opacity: isStackDimmed ? 0.2 : 1, transition: 'opacity 0.2s ease' }}
                onMouseEnter={() => onHoverStack && onHoverStack(stack)}
                onMouseLeave={() => onHoverStack && onHoverStack(null)}
                onClick={() => onStackClick && onStackClick(stack)}
              >
                {/* Invisible wider hit area for easier hovering */}
                <line
                  x1={x}
                  y1={STACK_LABEL_HEIGHT}
                  x2={x}
                  y2={STACK_LABEL_HEIGHT + gridHeight}
                  stroke="transparent"
                  strokeWidth={20}
                />
                {/* Highlight glow when hovered or selected */}
                {isStackHighlighted && (
                  <line
                    x1={x}
                    y1={STACK_LABEL_HEIGHT}
                    x2={x}
                    y2={STACK_LABEL_HEIGHT + gridHeight}
                    stroke={colors.color}
                    strokeWidth={12}
                    opacity={0.2}
                  />
                )}
                {/* Stack line (dotted) */}
                <line
                  x1={x}
                  y1={STACK_LABEL_HEIGHT}
                  x2={x}
                  y2={STACK_LABEL_HEIGHT + gridHeight}
                  stroke={colors.color}
                  strokeWidth={isStackHighlighted ? 4 : 2}
                  strokeDasharray={isStackHighlighted ? "none" : "6,4"}
                  opacity={isStackHighlighted ? 1 : 0.6}
                  style={{ transition: 'stroke-width 0.15s ease, opacity 0.15s ease' }}
                />
              </g>
            );
          })}

          {/* Horizontal Lines (every 5 floors) and Labels */}
          {floorLines.map((floorNum, idx) => {
            const floorSpacing = gridHeight / (floorLines.length - 1 || 1);
            const y = STACK_LABEL_HEIGHT + idx * floorSpacing;
            return (
              <g key={`floor-${floorNum}`}>
                {/* Horizontal line */}
                <line
                  x1={LABEL_WIDTH}
                  y1={y}
                  x2={LABEL_WIDTH + gridWidth}
                  y2={y}
                  stroke="#d1d5db"
                  strokeWidth={1}
                />
                {/* Floor label (left side). The top line is the artificial
                    headroom floor, so label it "Top" instead of a number. */}
                <text
                  x={LABEL_WIDTH - 6}
                  y={y + 6}
                  textAnchor="end"
                  fontSize={idx === 0 ? "14" : "18"}
                  fill="#374151"
                  fontWeight="600"
                >
                  {idx === 0 ? 'Top' : floorNum}
                </text>
              </g>
            );
          })}

          {/* Unit indicators - circles with unit number inside, positioned at actual floor */}
          {unitsData.map((unit, idx) => {
            const stackIdx = stacks.indexOf(unit.stack_number);
            if (stackIdx === -1) return null;

            // Extract floor number from unit number (e.g., 501 = floor 5, 1001 = floor 10)
            const floor = getFloorFromUnit(unit.unit_number);
            if (floor === 0) return null;

            // Calculate Y position based on actual floor
            // floorLines[0] is the max (top), floorLines[last] is the min (bottom)
            const maxFloor = floorLines[0];
            const minFloor = floorLines[floorLines.length - 1];
            const floorRange = maxFloor - minFloor;
            
            // Position proportionally (top = maxFloor, bottom = minFloor)
            const yRatio = floorRange > 0 ? (maxFloor - floor) / floorRange : 0.5;
            const cy = STACK_LABEL_HEIGHT + yRatio * gridHeight;
            
            const cx = LABEL_WIDTH + PADDING + stackIdx * STACK_SPACING;
            
            // Check if this unit is hovered or matches search
            const isHovered = hoveredUnitId === unit.objectId || isUnitSearchMatch(unit);
            
            // Check if this unit matches the status filter
            const matchesStatusFilter = !statusFilter || 
              (statusFilter === 'ok' && !unit.has_issues) || 
              (statusFilter === 'issue' && unit.has_issues);
            
            // Check if this unit belongs to any of the selected stacks
            const matchesSelectedStack = selectedStacks.length === 0 || selectedStacks.includes(unit.stack_number);
            
            // Dim units that don't match the filter OR don't match any selected stack
            const opacity = (statusFilter && !matchesStatusFilter) || (selectedStacks.length > 0 && !matchesSelectedStack) ? 0.2 : 1;
            const isHighlightedByFilter = statusFilter && matchesStatusFilter;
            
            // Green for OK, Red for issues
            const fillColor = unit.has_issues ? '#fef2f2' : '#ecfdf5';
            const strokeColor = unit.has_issues ? '#dc2626' : '#16a34a';
            const textColor = unit.has_issues ? '#dc2626' : '#16a34a';

            return (
              <g 
                key={`unit-${idx}`} 
                style={{ cursor: 'pointer', opacity, transition: 'opacity 0.2s ease' }}
                onMouseEnter={() => onHoverUnit && onHoverUnit(unit.objectId)}
                onMouseLeave={() => onHoverUnit && onHoverUnit(null)}
                onClick={() => onUnitClick && onUnitClick(unit)}
              >
                {/* Highlight ring when hovered or filtered */}
                {(isHovered || isHighlightedByFilter) && (
                  <rect
                    x={cx - 33}
                    y={cy - 18}
                    width={66}
                    height={36}
                    rx={18}
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    opacity={0.6}
                  />
                )}
                <rect
                  x={cx - 29}
                  y={cy - 14}
                  width={58}
                  height={28}
                  rx={14}
                  fill={fillColor}
                  stroke={isHovered ? '#3b82f6' : strokeColor}
                  strokeWidth={isHovered ? 2 : 1}
                />
                <text
                  x={cx}
                  y={cy + 5}
                  textAnchor="middle"
                  fontSize="16"
                  fontWeight="700"
                  fill={textColor}
                >
                  {unit.unit_number}
                </text>
              </g>
            );
          })}

          {/* Stack labels - rendered after units to be on top */}
          {stacks.map((stack, idx) => {
            const colors = getStackColor(stack);
            const x = LABEL_WIDTH + PADDING + idx * STACK_SPACING;
            const isStackSelected = selectedStacks.includes(stack);
            const isStackHighlighted = hoveredStackId === stack || isStackSelected;
            const isStackDimmed = selectedStacks.length > 0 && !isStackSelected;
            
            return (
              <g 
                key={`stack-label-${stack}`}
                style={{ cursor: 'pointer', opacity: isStackDimmed ? 0.2 : 1, transition: 'opacity 0.2s ease' }}
                onClick={() => onStackClick && onStackClick(stack)}
                onMouseEnter={() => onHoverStack && onHoverStack(stack)}
                onMouseLeave={() => onHoverStack && onHoverStack(null)}
                transform={shouldAngleLabels ? `rotate(-35, ${x}, 55)` : undefined}
              >
                <rect
                  x={x - 26}
                  y={38}
                  width={52}
                  height={32}
                  rx={16}
                  fill={isStackHighlighted ? colors.color : colors.bg}
                  stroke={colors.border}
                  strokeWidth={isStackHighlighted ? 2 : 1}
                  style={{ transition: 'fill 0.15s ease' }}
                />
                <text
                  x={x}
                  y={60}
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="700"
                  fill={isStackHighlighted ? '#fff' : colors.color}
                  style={{ transition: 'fill 0.15s ease' }}
                >
                  {stack}
                </text>
              </g>
            );
          })}

          {/* Tooltip layer - rendered last to appear on top of everything */}
          {(() => {
            // Find unit by hover OR by search match
            let hoveredUnit = unitsData.find(u => u.objectId === hoveredUnitId);
            if (!hoveredUnit) {
              // Check for search match
              hoveredUnit = unitsData.find(u => isUnitSearchMatch(u));
            }
            if (!hoveredUnit) return null;
            
            const stackIdx = stacks.indexOf(hoveredUnit.stack_number);
            if (stackIdx === -1) return null;
            
            const floor = getFloorFromUnit(hoveredUnit.unit_number);
            if (floor === 0) return null;
            
            const maxFloor = floorLines[0];
            const minFloor = floorLines[floorLines.length - 1];
            const floorRange = maxFloor - minFloor;
            const yRatio = floorRange > 0 ? (maxFloor - floor) / floorRange : 0.5;
            const cy = STACK_LABEL_HEIGHT + yRatio * gridHeight;
            const cx = LABEL_WIDTH + PADDING + stackIdx * STACK_SPACING;
            
            // Show tooltip on left for rightmost 3 columns
            const isRightSide = stackIdx >= stacks.length - 3;
            const tooltipWidth = 180;
            const tooltipX = isRightSide ? cx - tooltipWidth - 40 : cx + 40;
            const tooltipY = cy - 50;
            
            return (
              <g 
                style={{ cursor: 'pointer' }}
                onClick={() => onUnitClick && onUnitClick(hoveredUnit)}
              >
                {/* Tooltip background */}
                <rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={100}
                  rx={8}
                  fill="white"
                  stroke="#d1d5db"
                  strokeWidth={2}
                  style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}
                />
                {/* Tooltip content */}
                <text x={tooltipX + 12} y={tooltipY + 24} fontSize="14" fill="#6b7280">
                  Unit: <tspan fontWeight="700" fontSize="16" fill="#1f2937">{hoveredUnit.unit_number}</tspan>
                </text>
                <text x={tooltipX + 12} y={tooltipY + 46} fontSize="14" fill="#6b7280">
                  Stack: <tspan fontWeight="700" fontSize="16" fill="#1f2937">{hoveredUnit.stack_number}</tspan>
                </text>
                <text x={tooltipX + 12} y={tooltipY + 68} fontSize="14" fill="#6b7280">
                  Status: <tspan fontWeight="700" fontSize="16" fill={hoveredUnit.has_issues ? '#dc2626' : '#16a34a'}>
                    {hoveredUnit.has_issues ? 'Issue Found' : 'OK'}
                  </tspan>
                </text>
                {/* Click hint */}
                <text x={tooltipX + 12} y={tooltipY + 88} fontSize="11" fill="#9ca3af" fontStyle="italic">
                  Click to view details
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}

function StackCleaning() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [buildingName, setBuildingName] = useState("");
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState("");
  const [maintenanceCycles, setMaintenanceCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [hasStackCleaningContract, setHasStackCleaningContract] = useState(null);
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Data state
  const [unitsData, setUnitsData] = useState([]);
  const [hoveredUnitId, setHoveredUnitId] = useState(null); // For hover interaction between table and building
  const [hoveredStackId, setHoveredStackId] = useState(null); // For hover interaction on stack rows
  const [selectedStacks, setSelectedStacks] = useState([]); // For tracking selected/expanded stacks
  const [selectedUnitForModal, setSelectedUnitForModal] = useState(null); // For building visualization click
  const [buildingStatusFilter, setBuildingStatusFilter] = useState(null); // 'ok', 'issue', or null for all

  // Table state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('stack_number');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Auto-highlight unit when search matches
  useEffect(() => {
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      const matchingUnit = unitsData.find(u => 
        (u.unit_number || '').toLowerCase().includes(searchLower)
      );
      if (matchingUnit) {
        setHoveredUnitId(matchingUnit.objectId);
      }
    } else {
      // Clear hover when search is cleared
      setHoveredUnitId(null);
    }
  }, [searchTerm, unitsData]);

  // Responsive state for tablet breakpoint
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024);
  
  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Read the token & buildingId from localStorage
  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || "";
  let buildingId = "";
  
  if (userStr) {
    try {
      const userObj = JSON.parse(userStr);
      buildingId = userObj.buildingId || "";
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }

  // Fetch building data and stack cleaning data
  useEffect(() => {
    const fetchData = async () => {
      if (!buildingId || !token) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch building data
        const buildingResp = await fetch(
          `${API_BASE_URL}/api/getBuildingById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );

        if (buildingResp.ok) {
          const buildingData = await buildingResp.json();
          setBuildingName(buildingData.name || "Unknown");
          setBuildingManagerName(Object.prototype.hasOwnProperty.call(buildingData, 'manager') ? buildingData.manager || '' : buildingData.managerProfile?.manager || '');
          setBuildingPhotoUrl(buildingData.photo_url || "");
          
          // Check if building has stack cleaning contract
          const contracts = buildingData.contracts || [];
          const hasContract = contracts.includes("stack-cleaning");
          console.log("[StackCleaning] Building contracts:", contracts);
          console.log("[StackCleaning] Has stack-cleaning contract:", hasContract);
          console.log("[StackCleaning] Current buildingId:", buildingId);
          setHasStackCleaningContract(hasContract);
          setHasProjectContract(contracts.includes('project'));
          setHasValveAuditContract(contracts.includes('valve-audit'));

          // Fetch maintenance cycles
          const cyclesResp = await fetch(
            `${API_BASE_URL}/api/getMaintenanceInspections`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
              body: JSON.stringify({ buildingId })
            }
          );

          let stackCleaningCycles = [];
          if (cyclesResp.ok) {
            const cyclesData = await cyclesResp.json();
            // Filter for kitchen stack cycles only
            stackCleaningCycles = (cyclesData.maintenanceInspections || [])
              .filter(c => String(c.buildingId) === String(buildingId))
              .filter(c => isKitchenStackCycle(c));
            
            console.log("Kitchen Stack Cycles found:", stackCleaningCycles);
            
            // Map and sort cycles
            stackCleaningCycles = stackCleaningCycles.map(cycle => ({
              ...cycle,
              maintenanceID: cycle.maintenanceID || cycle.objectId,
              cycle: cycle.cycle || cycle.cycleName || cycle.name || "Unnamed Cycle"
            }));
            
            // Sort by createdAt descending (most recent first)
            stackCleaningCycles.sort((a, b) => {
              const dateA = new Date(a.createdAt || a.created_at || 0);
              const dateB = new Date(b.createdAt || b.created_at || 0);
              return dateB - dateA;
            });
          }
          
          setMaintenanceCycles(stackCleaningCycles);
          const mostRecentCycle = stackCleaningCycles[0];
          setSelectedCycle(mostRecentCycle?.cycle || "");
          setSelectedMaintenance(mostRecentCycle || null);

          // If we have kitchen stack cycles, fetch the units for the most recent one
          if (mostRecentCycle) {
            const maintenanceId = mostRecentCycle.maintenanceID || mostRecentCycle.objectId;
            console.log("[StackCleaning] Fetching units for maintenanceId:", maintenanceId);
            await fetchStackCleaningData(maintenanceId);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setHasStackCleaningContract(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, buildingId]);

  // Fetch stack cleaning units from API by maintenanceId and buildingId
  const fetchStackCleaningData = async (maintenanceId) => {
    if (!maintenanceId) {
      console.log("[StackCleaning] No maintenanceId provided, skipping fetch");
      return;
    }
    
    try {
      console.log("[StackCleaning] Fetching units for maintenanceId:", maintenanceId, "buildingId:", buildingId);
      
      const response = await fetch(
        `${API_BASE_URL}/api/stack-cleaning/units`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ maintenanceId, buildingId })
        }
      );

      console.log("[StackCleaning] API response status:", response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log("[StackCleaning] API response data:", data);
        if (data.success) {
          setUnitsData(data.units || []);
          console.log("[StackCleaning] Units loaded:", data.units?.length || 0);
        }
      } else {
        const errorText = await response.text();
        console.error("[StackCleaning] API error:", errorText);
      }
    } catch (err) {
      console.error("Error fetching stack cleaning data:", err);
    }
  };

  // Handle cycle change from dropdown
  const handleCycleChange = async (newCycle) => {
    if (!newCycle || !maintenanceCycles.length) return;
    
    const selectedCycleObj = maintenanceCycles.find(c => c.cycle === newCycle);
    if (!selectedCycleObj) return;
    
    setSelectedCycle(newCycle);
    setSelectedMaintenance(selectedCycleObj);
    
    const maintenanceId = selectedCycleObj.maintenanceID || selectedCycleObj.objectId;
    console.log("[StackCleaning] Cycle changed, fetching units for maintenanceId:", maintenanceId);
    await fetchStackCleaningData(maintenanceId);
  };

  const handleNavigate = (key) => {
    switch (key) {
      case 'hvac':
        navigate('/');
        break;
      case 'mech':
        navigate('/mechanical-room');
        break;
      case 'dryerVents':
        navigate('/dryer-vents');
        break;
      case 'stackCleaning':
        navigate('/stack-cleaning');
        break;
      case 'waterSavings':
        navigate('/water-savings');
        break;
      case 'reports':
        navigate('/reports');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'valveAudit':
        navigate('/valve-audit');
        break;
      default:
        break;
    }
  };

  // Filter data by search term
  const filteredData = unitsData.filter((row) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const stackNum = (row.stack_number || '').toLowerCase();
    const unitNum = (row.unit_number || '').toLowerCase();
    const notes = (row.notes || '').toLowerCase();
    const issueNotes = (row.issue_notes || '').toLowerCase();
    
    return stackNum.includes(searchLower) || 
           unitNum.includes(searchLower) || 
           notes.includes(searchLower) ||
           issueNotes.includes(searchLower);
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];

    // Handle boolean sorting (has_issues)
    if (sortColumn === 'has_issues') {
      valA = valA ? 1 : 0;
      valB = valB ? 1 : 0;
    }

    // Handle string sorting
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    // Handle undefined/null values
    if (valA == null) valA = '';
    if (valB == null) valB = '';

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = sortedData.slice(startIndex, endIndex);

  // Handlers
  function handleRowsPerPageChange(e) {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }
  
  function handlePrevPage() {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  }
  
  function handleNextPage() {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  }
  
  function handleSort(columnKey) {
    if (sortColumn === columnKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  }

  const EmptyState = () => (
    <div style={{ width: '90%', margin: '2rem auto 0', backgroundColor: '#f8f9fa' }}>
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Body className="text-center p-5">
          <div style={{ fontSize: '3rem', color: '#224982', marginBottom: '1.5rem' }}>
            <FaTint />
          </div>
          <Card.Title style={{ 
            fontSize: '2rem', 
            marginBottom: '1.5rem', 
            color: '#224982',
            fontWeight: '600'
          }}>
            Kitchen Stack Cleaning
          </Card.Title>
          <Card.Text style={{ 
            color: '#596272', 
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: '1.6',
            maxWidth: '800px',
            margin: '0 auto 2rem'
          }}>
            Keep your building's kitchen stacks clean and flowing with Hudson's stack cleaning solution.
            Get professional cleaning services, inspections, and maintenance tracking to ensure
            proper drainage and maintain a healthy building environment.
          </Card.Text>
          <div style={{ fontSize: '1.15rem', color: '#224982', fontWeight: 500 }}>
            Email <a href="mailto:service@hudsonhvac.ca">service@hudsonhvac.ca</a> for a free proposal
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const LoadingState = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <Spinner animation="border" role="status" style={{ color: '#224982' }}>
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex' }}>
        <div
          className="dashboard-sidebar-host"
          onMouseEnter={() => setCollapsed(false)}
          onMouseLeave={() => setCollapsed(true)}
          style={{ height: '100vh' }}
        >
          <Sidebar
            managerName={buildingManagerName}
            activeKey="stackCleaning"
            onNavigate={handleNavigate}
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
            buildingName={buildingName}
            buildingPhotoUrl={buildingPhotoUrl}
            showProjectsTab={hasProjectContract}
            showValveAuditTab={hasValveAuditContract}
          />
        </div>
        <div
          className="dashboard-content manager-contract-page"
          style={{
            flex: 1,
            minWidth: 0,
            marginLeft: collapsed ? 64 : 220,
            transition: 'margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'translate3d(0,0,0)',
            willChange: 'margin-left',
            backfaceVisibility: 'hidden',
          }}
        >
          <DashboardNavbar
            logoutInSidebar
            buildingName={buildingName}
            buildingId={buildingId}
            contractLabel="Stack Cleaning"
          />

          <BlueHeader
            maintenanceCycles={maintenanceCycles}
            selectedCycle={selectedCycle}
            setSelectedCycle={handleCycleChange}
            selectedMaintenance={selectedMaintenance}
            contractLabel="Stack Cleaning"
            buildingName={buildingName}
          />

          {isLoading ? (
            <LoadingState />
          ) : maintenanceCycles.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="stack-cleaning-content" style={{
              width: '90%',
              margin: '0 auto',
              paddingTop: '1rem',
              paddingBottom: '1rem'
            }}>
              {/* Overview Summary Cards */}
              {(() => {
                // Calculate summary stats
                const uniqueStacks = [...new Set(unitsData.map(u => u.stack_number))];
                const totalStacks = uniqueStacks.length;
                const totalUnits = unitsData.length;
                
                // Stack health: % of stacks with NO issues
                const stacksWithIssues = uniqueStacks.filter(stack => 
                  unitsData.some(u => u.stack_number === stack && u.has_issues)
                ).length;
                const stackHealth = totalStacks > 0 ? Math.round(((totalStacks - stacksWithIssues) / totalStacks) * 100) : 100;
                
                // Unit health: % of units with NO issues
                const unitsWithIssues = unitsData.filter(u => u.has_issues).length;
                const unitHealth = totalUnits > 0 ? Math.round(((totalUnits - unitsWithIssues) / totalUnits) * 100) : 100;
                
                const unitsWithNoIssues = totalUnits - unitsWithIssues;
                
                const cards = [
                  { title: 'Total Stacks', value: totalStacks, color: '#3b82f6', icon: FaLayerGroup },
                  { title: 'Units with No Issues', value: unitsWithNoIssues, color: '#10b981', icon: FaCheckCircle },
                  { title: 'Units with Issues', value: unitsWithIssues, color: '#ea580c', icon: FaHeartbeat },
                  { title: 'Unit Health', value: unitHealth, color: null, icon: FaClipboardCheck, isPercent: true }
                ];
                
                return (
                  <div style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    marginBottom: '1.5rem',
                    flexWrap: 'wrap'
                  }}>
                    {cards.map((card, index) => {
                      const IconComponent = card.icon;
                      return (
                        <div
                          key={index}
                          style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '1.25rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            flex: '1',
                            minWidth: '180px',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          {card.isPercent ? (
                            <CircularProgress percentage={card.value} size={70} />
                          ) : (
                            <div style={{
                              width: '70px',
                              height: '70px',
                              borderRadius: '50%',
                              border: `5px solid ${card.color}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'transparent'
                            }}>
                              <span style={{ fontSize: '1.75rem', fontWeight: '700', color: '#374151' }}>
                                {card.value}
                              </span>
                            </div>
                          )}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: '0.5rem',
                            fontSize: '0.8rem',
                            color: '#6b7280',
                            fontWeight: '500'
                          }}>
                            <IconComponent style={{ fontSize: '0.875rem', color: card.isPercent ? '#6b7280' : card.color }} />
                            {card.title}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div style={{ 
                display: 'flex', 
                flexDirection: isTablet ? 'column' : 'row',
                gap: '1.5rem', 
                alignItems: isTablet ? 'center' : 'flex-start'
              }}>
                {/* Building Wireframe - on top for tablet, right side for desktop (60%) */}
                <div style={{ 
                  flex: isTablet ? 'none' : '0 0 60%',
                  width: isTablet ? '100%' : 'auto',
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  padding: '0.25rem',
                  position: isTablet ? 'relative' : 'sticky',
                  top: isTablet ? 'auto' : '0',
                  height: isTablet ? 'auto' : '740px',
                  maxHeight: isTablet ? 'none' : '740px',
                  overflow: 'hidden',
                  order: isTablet ? 1 : 2,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <BuildingWireframe 
                    unitsData={unitsData} 
                    getStackColor={getStackColor} 
                    hoveredUnitId={hoveredUnitId}
                    onHoverUnit={setHoveredUnitId}
                    onUnitClick={setSelectedUnitForModal}
                    hoveredStackId={hoveredStackId}
                    onHoverStack={setHoveredStackId}
                    searchTerm={searchTerm}
                    buildingName={buildingName}
                    statusFilter={buildingStatusFilter}
                    onStatusFilterChange={setBuildingStatusFilter}
                    selectedStacks={selectedStacks}
                    onStackClick={(stack) => {
                      // Toggle stack in the selected list
                      setSelectedStacks(prev => 
                        prev.includes(stack) 
                          ? prev.filter(s => s !== stack)
                          : [...prev, stack]
                      );
                    }}
                  />
                </div>

                {/* Table and Filters - below for tablet, left side for desktop (40%) */}
                <div style={{ 
                  flex: isTablet ? 'none' : '0 0 calc(40% - 1.5rem)', 
                  width: isTablet ? '100%' : 'auto',
                  minWidth: 0,
                  order: isTablet ? 2 : 1
                }}>
                  {/* Search Bar */}
                  <div style={{ marginBottom: '1rem' }}>
                    <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                  </div>

                  {/* Stack Cleaning Table */}
                  <StackCleaningTable
                    unitsData={unitsData}
                    sortedData={sortedData}
                    hoveredUnitId={hoveredUnitId}
                    onHoverUnit={setHoveredUnitId}
                    hoveredStackId={hoveredStackId}
                    onHoverStack={setHoveredStackId}
                    searchTerm={searchTerm}
                    externalSelectedUnit={selectedUnitForModal}
                    onExternalSelectedUnitClear={() => setSelectedUnitForModal(null)}
                    onExpandedStacksChange={setSelectedStacks}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'center', color: '#888', fontSize: '0.95rem', margin: '1.5rem 0 0.5rem 0' }}>
        © 2025 Hudson Condominium Solutions Inc.
      </div>
    </>
  );
}

export default StackCleaning;
